import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, FormEventHandler, ReactNode } from 'react';
import { dialog, price, productCard, semanticColor, storeCard } from '@beautyburst/tokens';
import { Badge, Button, EmptyState, SellerBadge, StockBadge } from './primitives.tsx';
import type { AvailabilityState, StockState } from './primitives.tsx';
import { cardStyle } from './style-utils.ts';

/**
 * Customer commerce components.
 *
 * These render the approved Phase 3 customer surfaces on the Phase 1 design
 * system. They add no new colours, radii or type scales: every value comes from
 * `@beautyburst/tokens`. Do not restyle the approved customer aesthetic here.
 *
 * Two rules are enforced structurally rather than by convention:
 *
 * 1. Seller identity is a required prop on every product and store surface, so
 *    it is not possible to render a card without saying who is selling.
 * 2. Unknown values are typed `null`, never `0` or a placeholder string, so an
 *    absent rating, distance or delivery estimate cannot read as real data.
 */

const money = (cents: number, currency: string): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

/** Distance is rendered only when genuinely known. */
export function formatDistance(miles: number | null): string | null {
  if (miles === null) return null;
  return miles < 10 ? `${miles.toFixed(1)} mi away` : `${Math.round(miles)} mi away`;
}

export interface PriceProps {
  priceCents: number;
  /** A server-composed active-sale decision. Defaults false for older callers. */
  saleActive?: boolean;
  /** Sale price supplied by the server; rendered only when `saleActive` is true. */
  salePriceCents?: number | null;
  currency?: string;
}

/** Sale pricing announces both prices so the saving is not visual-only. */
export function Price({
  priceCents,
  saleActive = false,
  salePriceCents = null,
  currency = 'USD',
}: PriceProps) {
  const onSale = saleActive && salePriceCents !== null && salePriceCents < priceCents;
  if (!onSale) {
    return (
      <p
        data-bb-component="price"
        style={{
          margin: 0,
          fontSize: price.regular.fontSize,
          fontWeight: price.regular.fontWeight,
          color: price.regular.color,
        }}
      >
        {money(priceCents, currency)}
      </p>
    );
  }
  return (
    <p
      data-bb-component="price"
      style={{ margin: 0, display: 'flex', alignItems: 'baseline', gap: '0.375rem' }}
    >
      <span
        style={{
          fontSize: price.sale.fontSize,
          fontWeight: price.sale.fontWeight,
          color: price.sale.color,
        }}
      >
        {money(salePriceCents, currency)}
      </span>
      <span
        style={{
          fontSize: '0.8125rem',
          color: semanticColor.textSecondary,
          textDecoration: 'line-through',
        }}
      >
        <span style={{ position: 'absolute', left: '-9999px' }}>Was </span>
        {money(priceCents, currency)}
      </span>
    </p>
  );
}

export interface FavoriteButtonProps {
  favorited: boolean;
  /** Name of the thing being favourited, for the accessible label. */
  subject: string;
  onToggle?: () => void;
  disabled?: boolean;
}

/** Icon-only control, so it carries a real accessible name and pressed state. */
export function FavoriteButton({ favorited, subject, onToggle, disabled }: FavoriteButtonProps) {
  return (
    <button
      type="button"
      data-bb-component="favorite-button"
      aria-pressed={favorited}
      aria-label={favorited ? `Remove ${subject} from favorites` : `Save ${subject} to favorites`}
      disabled={disabled}
      onClick={onToggle}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '44px',
        minHeight: '44px',
        border: `1px solid ${semanticColor.borderDefault}`,
        borderRadius: '999px',
        background: semanticColor.surfaceCard,
        color: favorited ? semanticColor.brandPrimary : semanticColor.textSecondary,
        cursor: disabled === true ? 'not-allowed' : 'pointer',
        fontSize: '1.125rem',
        lineHeight: 1,
      }}
    >
      <span aria-hidden="true">{favorited ? '♥' : '♡'}</span>
    </button>
  );
}

export interface ProductCardProps {
  /** Product page href. */
  href: string;
  imageUrl: string | null;
  /** Required: never render a product image without alt text. */
  imageAlt: string;
  brand: string;
  name: string;
  /** Variant summary such as "1B · 24 inch", or null for single-variant products. */
  variantLabel?: string | null;
  priceCents: number;
  saleActive?: boolean;
  salePriceCents?: number | null;
  currency?: string;
  /** Retailer display name, or null for Beauty Burst Direct. Required. */
  retailerName: string | null;
  storeName?: string | null;
  stockState: StockState;
  /** Null when the customer location or the store coordinates are unknown. */
  distanceMiles?: number | null;
  /** Null until a delivery provider is connected. Never invent an estimate. */
  deliveryEstimate?: string | null;
  availability?: AvailabilityState | null;
  /**
   * Slot for an availability badge that was already composed by the server.
   * Availability wording is the server composer's decision, so surfaces that
   * receive a composed label pass the rendered badge here instead of handing
   * this component a state to map. It renders inside the card's badge row; a
   * badge rendered as a sibling of the card falls outside the card border.
   */
  availabilitySlot?: ReactNode;
  membershipEligible?: boolean;
  favorited?: boolean;
  onToggleFavorite?: () => void;
  onAddToBag?: () => void;
  /** False when the item cannot currently be added, with a stated reason. */
  canAddToBag?: boolean;
  addToBagBlockedReason?: string | null;
}

export function ProductCard({
  href,
  imageUrl,
  imageAlt,
  brand,
  name,
  variantLabel = null,
  priceCents,
  saleActive = false,
  salePriceCents = null,
  currency = 'USD',
  retailerName,
  storeName = null,
  stockState,
  distanceMiles = null,
  deliveryEstimate = null,
  availability = null,
  availabilitySlot = null,
  membershipEligible = false,
  favorited = false,
  onToggleFavorite,
  onAddToBag,
  canAddToBag = true,
  addToBagBlockedReason = null,
}: ProductCardProps) {
  const distance = formatDistance(distanceMiles);
  // The seller badge above already names the seller. When the store label repeats
  // that name verbatim the card shows the retailer twice, so the duplicate is
  // dropped rather than the mandatory seller badge.
  const storeLabel = storeName !== null && storeName === retailerName ? null : storeName;
  const secondaryParts = [storeLabel, distance, deliveryEstimate].filter(
    (part): part is string => part !== null,
  );
  const secondaryLine = secondaryParts.length === 0 ? null : secondaryParts.join(' · ');
  const blocked = canAddToBag === false || stockState === 'OUT_OF_STOCK';
  // A product with no media, or whose media fails to load, still gets a deliberate
  // branded tile. It must never degrade to an unexplained blank rectangle.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);
  const showImageFallback = imageUrl === null || imageFailed;
  return (
    <article
      data-bb-component="product-card"
      style={{
        background: productCard.background,
        borderRadius: productCard.radius,
        boxShadow: productCard.shadow,
        border: `1px solid ${semanticColor.borderDefault}`,
        padding: productCard.padding,
        display: 'grid',
        // Grid items default to `min-width: auto`, so a single unbreakable child
        // (the seller badge is the usual culprit) widens the column past the
        // card's own content box and every row then bleeds over the border.
        // `minmax(0, 1fr)` caps the column so children shrink instead.
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: productCard.gap,
      }}
    >
      <div style={{ position: 'relative' }}>
        {/* text-decoration propagates from the anchor and cannot be cancelled by a
            descendant, so the media link itself must opt out or the fallback
            caption renders underlined and reads as a link label. */}
        <a href={href} style={{ display: 'block', textDecoration: 'none' }}>
          {showImageFallback ? (
            <div
              role="img"
              aria-label={imageAlt}
              data-bb-component="product-image-fallback"
              style={{
                aspectRatio: productCard.imageRatio,
                maxHeight: productCard.imageMaxHeight,
                borderRadius: productCard.imageRadius,
                background: semanticColor.surfaceCanvasCustomer,
                border: `1px solid ${semanticColor.borderDefault}`,
                boxSizing: 'border-box',
                // Horizontal padding keeps the caption clear of the favourite
                // button, which is absolutely positioned over the top-right.
                padding: '2.25rem 0.625rem 0.75rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.125rem',
                textAlign: 'center',
                overflow: 'hidden',
                // The tile sits inside the card's anchor, which underlines its
                // text. This is a picture, not a link label.
                textDecoration: 'none',
              }}
            >
              {/* role="img" + aria-label already names this tile, so the visible
                  caption is presentational and must not be announced twice. */}
              <span
                aria-hidden="true"
                style={{
                  // Narrow cards are the common case, so the wordmark is sized to
                  // fit a single line inside the smallest track without clipping.
                  fontSize: '0.5625rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: semanticColor.textLink,
                  textDecoration: 'none',
                  maxWidth: '100%',
                }}
              >
                Beauty Burst
              </span>
              <span
                aria-hidden="true"
                style={{
                  fontSize: '0.6875rem',
                  lineHeight: 1.3,
                  color: semanticColor.textSecondary,
                  textDecoration: 'none',
                  maxWidth: '100%',
                }}
              >
                Product photo coming soon
              </span>
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={imageAlt}
              loading="lazy"
              onError={() => setImageFailed(true)}
              style={{
                width: '100%',
                aspectRatio: productCard.imageRatio,
                maxHeight: productCard.imageMaxHeight,
                objectFit: 'cover',
                borderRadius: productCard.imageRadius,
                background: semanticColor.surfaceSubtle,
              }}
            />
          )}
        </a>
        <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
          <FavoriteButton
            favorited={favorited}
            subject={`${brand} ${name}`}
            onToggle={onToggleFavorite}
          />
        </div>
      </div>

      <p
        style={{
          margin: 0,
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: semanticColor.textSecondary,
        }}
      >
        {brand}
      </p>
      <h3 style={{ margin: 0, fontSize: '0.9375rem', lineHeight: 1.35, fontWeight: 600 }}>
        <a href={href} style={{ color: semanticColor.textPrimary, textDecoration: 'none' }}>
          {name}
        </a>
      </h3>
      {variantLabel === null ? null : (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: semanticColor.textSecondary }}>
          {variantLabel}
        </p>
      )}

      <Price
        priceCents={priceCents}
        saleActive={saleActive}
        salePriceCents={salePriceCents}
        currency={currency}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
        <SellerBadge retailerName={retailerName} />
        <StockBadge state={stockState} />
        {availability === null ? null : <AvailabilityPill state={availability} />}
        {availabilitySlot}
        {membershipEligible ? <Badge variant="membership">Beauty Burst+ eligible</Badge> : null}
      </div>

      {secondaryLine === null ? null : (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: semanticColor.textSecondary }}>
          {secondaryLine}
        </p>
      )}

      <Button
        variant="primary"
        size="sm"
        disabled={blocked}
        onClick={onAddToBag}
        aria-label={`Add ${brand} ${name} to bag`}
      >
        Add to Bag
      </Button>
      {blocked && addToBagBlockedReason !== null ? (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: semanticColor.textSecondary }}>
          {addToBagBlockedReason}
        </p>
      ) : null}
    </article>
  );
}

/** Availability pill used inside cards; wraps the shared availability vocabulary. */
export function AvailabilityPill({ state }: { state: AvailabilityState }) {
  const label: Record<AvailabilityState, string> = {
    OPEN_NOW: 'Open now',
    OPEN_LATE: 'Open late',
    OVERNIGHT: 'Available overnight',
    TWENTY_FOUR_HOUR: '24 hours',
    CLOSED: 'Closed',
  };
  const variant = {
    OPEN_NOW: 'openNow',
    OPEN_LATE: 'openLate',
    OVERNIGHT: 'overnight',
    TWENTY_FOUR_HOUR: 'overnight',
    CLOSED: 'outOfStock',
  } as const;
  return <Badge variant={variant[state]}>{label[state]}</Badge>;
}

export interface StoreCardProps {
  href: string;
  storeName: string;
  bannerUrl?: string | null;
  bannerAlt?: string;
  logoUrl?: string | null;
  tagline?: string | null;
  /** Null when the store has no reviews yet. Never render 0.0 as a rating. */
  averageRating?: number | null;
  reviewCount?: number;
  addressSummary?: string | null;
  distanceMiles?: number | null;
  availability?: AvailabilityState | null;
  pickupEnabled?: boolean;
  deliveryEnabled?: boolean;
  favorited?: boolean;
  onToggleFavorite?: () => void;
}

export function StoreCard({
  href,
  storeName,
  bannerUrl = null,
  bannerAlt,
  tagline = null,
  averageRating = null,
  reviewCount = 0,
  addressSummary = null,
  distanceMiles = null,
  availability = null,
  pickupEnabled = false,
  deliveryEnabled = false,
  favorited = false,
  onToggleFavorite,
}: StoreCardProps) {
  const distance = formatDistance(distanceMiles);
  return (
    <article
      data-bb-component="store-card"
      style={{
        background: storeCard.background,
        borderRadius: storeCard.radius,
        boxShadow: storeCard.shadow,
        border: `1px solid ${semanticColor.borderDefault}`,
        padding: storeCard.padding,
        display: 'grid',
        gap: '0.5rem',
      }}
    >
      {bannerUrl === null ? null : (
        <img
          src={bannerUrl}
          alt={bannerAlt ?? `${storeName} storefront`}
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: storeCard.bannerRatio,
            objectFit: 'cover',
            borderRadius: '0.75rem',
          }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
          <a href={href} style={{ color: semanticColor.textPrimary, textDecoration: 'none' }}>
            {storeName}
          </a>
        </h3>
        <FavoriteButton favorited={favorited} subject={storeName} onToggle={onToggleFavorite} />
      </div>
      {tagline === null ? null : (
        <p style={{ margin: 0, fontSize: '0.875rem', color: semanticColor.textSecondary }}>
          {tagline}
        </p>
      )}
      <p style={{ margin: 0, fontSize: '0.8125rem', color: semanticColor.textSecondary }}>
        {averageRating === null
          ? 'No reviews yet'
          : `${averageRating.toFixed(1)} out of 5 from ${reviewCount} reviews`}
      </p>
      {addressSummary === null && distance === null ? null : (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: semanticColor.textSecondary }}>
          {[addressSummary, distance].filter((part) => part !== null).join(' · ')}
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
        {availability === null ? null : <AvailabilityPill state={availability} />}
        {pickupEnabled ? <Badge variant="info">Pickup</Badge> : null}
        {deliveryEnabled ? <Badge variant="info">Delivery</Badge> : null}
      </div>
    </article>
  );
}

export interface CategoryTileProps {
  href: string;
  displayName: string;
  imageUrl?: string | null;
  /** Required when an image is supplied; the schema enforces it too. */
  imageAlt?: string;
}

export function CategoryTile({ href, displayName, imageUrl = null, imageAlt }: CategoryTileProps) {
  return (
    <a
      href={href}
      data-bb-component="category-tile"
      aria-label={displayName}
      style={{
        display: 'grid',
        gap: '0.5rem',
        padding: '0.75rem',
        minHeight: '44px',
        minWidth: 0,
        borderRadius: '1rem',
        border: `1px solid ${semanticColor.borderDefault}`,
        background: semanticColor.surfaceCard,
        color: semanticColor.textPrimary,
        textDecoration: 'none',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '0.875rem',
      }}
    >
      {imageUrl === null ? (
        <span
          aria-hidden="true"
          style={{
            minWidth: 0,
            minHeight: '9rem',
            borderRadius: '0.75rem',
            background: semanticColor.brandLavender,
            display: 'grid',
            gridTemplateRows: 'auto 1fr auto',
            gap: '0.5rem',
            padding: '0.875rem',
            color: semanticColor.brandContrast,
          }}
        >
          <span
            style={{
              minWidth: 0,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              overflowWrap: 'normal',
              wordBreak: 'normal',
              hyphens: 'none',
            }}
          >
            Beauty category
          </span>
          <strong
            style={{
              display: 'block',
              minWidth: 0,
              alignSelf: 'center',
              fontSize: '1.125rem',
              lineHeight: 1.15,
              overflowWrap: 'normal',
              wordBreak: 'normal',
              hyphens: 'none',
            }}
          >
            {displayName}
          </strong>
          <span
            style={{
              display: 'block',
              width: '2.5rem',
              borderTop: `3px solid ${semanticColor.brandPrimary}`,
            }}
          />
        </span>
      ) : (
        <img
          src={imageUrl}
          alt={imageAlt ?? ''}
          loading="lazy"
          style={{
            width: '100%',
            minWidth: 0,
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            borderRadius: '0.75rem',
          }}
        />
      )}
      {imageUrl === null ? null : displayName}
    </a>
  );
}

export interface SectionProps {
  title: string;
  subtitle?: string | null;
  ctaLabel?: string | null;
  ctaHref?: string | null;
  children: ReactNode;
}

/** Homepage section wrapper. Titles come from configuration, not code. */
export function Section({ title, subtitle = null, ctaLabel, ctaHref, children }: SectionProps) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      data-bb-component="section"
      style={{ marginBottom: '2rem' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '1rem',
          marginBottom: '0.75rem',
        }}
      >
        <div>
          <h2
            id={headingId}
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 700,
              color: semanticColor.textPrimary,
            }}
          >
            {title}
          </h2>
          {subtitle === null ? null : (
            <p
              style={{
                margin: '0.25rem 0 0',
                fontSize: '0.875rem',
                color: semanticColor.textSecondary,
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
        {ctaLabel === null ||
        ctaLabel === undefined ||
        ctaHref === null ||
        ctaHref === undefined ? null : (
          <a
            href={ctaHref}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: '44px',
              color: semanticColor.brandContrast,
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {ctaLabel}
          </a>
        )}
      </div>
      {children}
    </section>
  );
}

export interface CardGridProps {
  /** Accessible name for the collection. */
  label: string;
  children: ReactNode;
  minColumnWidth?: string;
}

export function CardGrid({ label, children, minColumnWidth = '180px' }: CardGridProps) {
  return (
    <ul
      aria-label={label}
      data-bb-component="card-grid"
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: '1rem',
        // `auto-fill`, never `auto-fit`. `auto-fit` collapses the empty tracks, so a
        // grid holding a single card stretches that card across the whole row. With a
        // 1:1 media ratio the image block then scales to the full row width and pushes
        // the name, price and seller far below the fold, which reads as a blank card.
        gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${minColumnWidth}), 1fr))`,
      }}
    >
      {children}
    </ul>
  );
}

export interface BottomSheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Mobile bottom sheet used for filters and pickers. Implements the dialog
 * pattern: labelled, modal, Escape closes, focus moves in on open and returns
 * to the trigger on close, and focus is trapped while open.
 */
export function BottomSheet({ open, title, onClose, children, footer }: BottomSheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement;
    panelRef.current?.focus();
    /*
     * The sheet is portalled to body, allowing every application-root sibling
     * to be hidden from assistive technology without hiding the dialog itself.
     */
    const hiddenSiblings = Array.from(document.body.children).filter(
      (child) => !child.hasAttribute('data-bb-component'),
    );
    const previousAriaHidden = new Map(
      hiddenSiblings.map((sibling) => [sibling, sibling.getAttribute('aria-hidden')]),
    );
    hiddenSiblings.forEach((sibling) => sibling.setAttribute('aria-hidden', 'true'));
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || panelRef.current === null) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousAriaHidden.forEach((previous, sibling) => {
        if (previous === null) sibling.removeAttribute('aria-hidden');
        else sibling.setAttribute('aria-hidden', previous);
      });
      const target = returnFocusRef.current;
      if (target instanceof HTMLElement) target.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  const sheet = (
    <div
      data-bb-component="bottom-sheet-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: dialog.overlay,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-bb-component="bottom-sheet"
        style={{
          background: dialog.background,
          borderTopLeftRadius: dialog.radius,
          borderTopRightRadius: dialog.radius,
          boxShadow: dialog.shadow,
          padding: dialog.padding,
          width: '100%',
          maxWidth: dialog.maxWidth,
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          <h2 id={titleId} style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>
            {title}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={`Close ${title}`}>
            Close
          </Button>
        </div>
        {children}
        {footer === undefined ? null : <div style={{ marginTop: '1rem' }}>{footer}</div>}
      </div>
    </div>
  );
  if (typeof document === 'undefined') return sheet;
  return createPortal(sheet, document.body);
}

export interface FilterOption {
  value: string;
  label: string;
  /** Result count where genuinely known, else null. Never guess a count. */
  count?: number | null;
}

export interface FilterGroupProps {
  legend: string;
  name: string;
  options: readonly FilterOption[];
  selected: readonly string[];
  onChange?: (value: string, checked: boolean) => void;
}

/** Checkbox filter group in a real fieldset, so the group name is announced. */
export function FilterGroup({ legend, name, options, selected, onChange }: FilterGroupProps) {
  const groupId = useId();
  if (options.length === 0) return null;
  return (
    <fieldset
      data-bb-component="filter-group"
      style={{ border: 'none', margin: 0, padding: '0 0 1rem' }}
    >
      <legend style={{ fontSize: '0.875rem', fontWeight: 700, padding: 0 }}>{legend}</legend>
      <div style={{ display: 'grid', gap: '0.25rem', marginTop: '0.5rem' }}>
        {options.map((option) => {
          const id = `${groupId}-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minHeight: '44px',
                fontSize: '0.875rem',
                color: semanticColor.textPrimary,
              }}
            >
              <input
                id={id}
                type="checkbox"
                name={name}
                value={option.value}
                checked={selected.includes(option.value)}
                onChange={(event) => onChange?.(option.value, event.currentTarget.checked)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.click();
                  }
                }}
                style={{ width: '1.125rem', height: '1.125rem' }}
              />
              <span>{option.label}</span>
              {option.count === null || option.count === undefined ? null : (
                <span style={{ color: semanticColor.textSecondary }}>({option.count})</span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export interface SellerGroupProps {
  /** "Glow Beauty Supply" or "Beauty Burst". Required. */
  sellerDisplayName: string;
  ownershipType: 'RETAILER' | 'BEAUTY_BURST_DIRECT';
  subtotalCents: number;
  itemCount: number;
  currency?: string;
  /** True when the cart holds more than one group. */
  mayShipSeparately?: boolean;
  fulfillmentOptions?: ReactNode;
  children: ReactNode;
}

/**
 * A cart seller group. Grouping is structural, not decorative: each group is a
 * future sub-order, and when more than one exists the customer is told plainly
 * that the groups may arrive separately.
 */
export function SellerGroup({
  sellerDisplayName,
  ownershipType,
  subtotalCents,
  itemCount,
  currency = 'USD',
  mayShipSeparately = false,
  fulfillmentOptions,
  children,
}: SellerGroupProps) {
  const headingId = useId();
  const direct = ownershipType === 'BEAUTY_BURST_DIRECT';
  return (
    <section
      aria-labelledby={headingId}
      data-bb-component="cart-seller-group"
      data-bb-ownership={ownershipType}
      style={{
        ...cardStyle(),
        marginBottom: '1rem',
        borderColor: direct ? semanticColor.brandLavender : semanticColor.borderDefault,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <h3 id={headingId} style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
          {direct ? 'Sold by Beauty Burst' : `Sold by ${sellerDisplayName}`}
        </h3>
        <p style={{ margin: 0, fontSize: '0.875rem', color: semanticColor.textSecondary }}>
          {itemCount === 1 ? '1 item' : `${itemCount} items`} · {money(subtotalCents, currency)}
        </p>
      </div>
      {mayShipSeparately ? (
        <p
          style={{
            margin: '0.5rem 0 0',
            fontSize: '0.8125rem',
            color: semanticColor.textSecondary,
          }}
        >
          This group may arrive separately from your other items.
        </p>
      ) : null}
      <div style={{ marginTop: '0.75rem' }}>{children}</div>
      {fulfillmentOptions === undefined ? null : (
        <div style={{ marginTop: '0.75rem' }}>{fulfillmentOptions}</div>
      )}
    </section>
  );
}

export interface ErrorSummaryProps {
  title?: string;
  /** Field errors, each linking to the input that needs attention. */
  errors: readonly { fieldId: string; message: string }[];
}

/** WCAG 2.2 error-summary pattern: focusable, announced, and linked to fields. */
export function ErrorSummary({ title = 'There is a problem', errors }: ErrorSummaryProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (errors.length > 0) ref.current?.focus();
  }, [errors.length]);
  if (errors.length === 0) return null;
  return (
    <div
      ref={ref}
      role="alert"
      tabIndex={-1}
      data-bb-component="error-summary"
      style={{
        ...cardStyle(),
        borderColor: semanticColor.statusDanger,
        marginBottom: '1rem',
      }}
    >
      <h2 style={{ margin: 0, fontSize: '1rem', color: semanticColor.statusDangerText }}>
        {title}
      </h2>
      <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
        {errors.map((error) => (
          <li key={error.fieldId} style={{ fontSize: '0.875rem' }}>
            <a
              href={`#${error.fieldId}`}
              style={{ color: semanticColor.statusDangerText }}
              onClick={(event) => {
                const field = document.getElementById(error.fieldId);
                if (field instanceof HTMLElement) {
                  event.preventDefault();
                  field.focus();
                }
              }}
            >
              {error.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface LocationPillProps {
  /** Resolved area name, or null when the customer has not set a location. */
  locationLabel: string | null;
  serviceability?: 'SERVICEABLE' | 'WAITLIST' | 'OUT_OF_AREA' | 'UNKNOWN';
  onChange?: () => void;
}

/** Location is always customer-initiated. Nothing here reads device location. */
export function LocationPill({
  locationLabel,
  serviceability = 'UNKNOWN',
  onChange,
}: LocationPillProps) {
  const label = locationLabel ?? 'Set your location';
  const note: Record<NonNullable<LocationPillProps['serviceability']>, string | null> = {
    SERVICEABLE: null,
    WAITLIST: 'Coming soon to your area',
    OUT_OF_AREA: 'Not yet serviced',
    UNKNOWN: null,
  };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
      <button
        type="button"
        data-bb-component="location-pill"
        onClick={onChange}
        aria-label={
          locationLabel === null
            ? 'Set your delivery location'
            : `Change location, currently ${locationLabel}`
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.375rem',
          minHeight: '44px',
          padding: '0 0.875rem',
          borderRadius: '999px',
          border: `1px solid ${semanticColor.borderStrong}`,
          background: semanticColor.surfaceCard,
          color: semanticColor.brandContrast,
          fontSize: '0.875rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <span aria-hidden="true">◎</span>
        {label}
      </button>
      {note[serviceability] === null ? null : (
        <Badge variant="warning">{note[serviceability]}</Badge>
      )}
    </div>
  );
}

export interface SearchBarProps {
  id?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  action?: string;
  name?: string;
  onSubmit?: FormEventHandler<HTMLFormElement>;
}

/** Search is a real labelled form, so it works without JavaScript and by keyboard. */
export function SearchBar({
  id = 'customer-search',
  label = 'Search beauty products and stores',
  placeholder = 'Search products, brands or stores',
  defaultValue,
  action = '/search',
  name = 'q',
  onSubmit,
}: SearchBarProps) {
  const style: CSSProperties = {
    flex: 1,
    minHeight: '44px',
    padding: '0 0.875rem',
    borderRadius: '999px',
    border: `1px solid ${semanticColor.borderDefault}`,
    background: semanticColor.surfaceCard,
    fontSize: '1rem',
  };
  return (
    <form
      role="search"
      action={action}
      method="get"
      data-bb-component="search-bar"
      onSubmit={onSubmit}
    >
      <label htmlFor={id} style={{ position: 'absolute', left: '-9999px' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          id={id}
          type="search"
          name={name}
          placeholder={placeholder}
          defaultValue={defaultValue}
          autoComplete="off"
          style={style}
        />
        <Button type="submit" variant="primary" size="md">
          Search
        </Button>
      </div>
    </form>
  );
}

export interface UnavailableSectionProps {
  title: string;
  /** Honest explanation. Never render an empty list implying the feature works. */
  reason: string;
}

/**
 * Placeholder for an account section owned by a later phase. This exists so a
 * not-yet-built feature reads as not-yet-built rather than as "you have none".
 */
export function UnavailableSection({ title, reason }: UnavailableSectionProps) {
  return <EmptyState title={title} description={reason} />;
}
